import { toast } from '@tempo-io/sui-sonner';
import type { TFunction } from 'i18next';
import { HTTPError } from 'ky';
import type { TypedFieldValue } from '@/components/common/custom-field-value/types';
import type { ItemPatchRequest, ItemV2 } from '@/lib/api/types/items';
import { unwrapMultiDto } from '@/lib/api/types/items';

type CustomFieldEntry = { customFieldId: string; value: string | number | boolean | null };
// TODO: MULTI_LINK values pass { url, description } objects through FieldValuesApi — consider
// widening `values` to `unknown[]` if link field editing is added to visualizations.
type MultiSelectFieldEntry = {
  customFieldId: string;
  values: (string | number | boolean)[];
  previousEntryIds?: number[];
};

export interface VisualizationItemUpdateInput {
  itemId: number;
  itemData: Partial<ItemPatchRequest> & Record<string, unknown>;
  customFieldData: CustomFieldEntry[] | null;
  multiSelectFieldData: MultiSelectFieldEntry[] | null;
}

export function createEmptyUpdateInput(itemId: number): VisualizationItemUpdateInput {
  return {
    itemId,
    itemData: {},
    customFieldData: null,
    multiSelectFieldData: null,
  };
}

export function buildItemFieldUpdate(
  itemId: number,
  field: string,
  value: unknown,
): VisualizationItemUpdateInput {
  return {
    itemId,
    itemData: { [field]: value },
    customFieldData: null,
    multiSelectFieldData: null,
  };
}

const MULTI_FIELD_TYPES = new Set([
  'MULTI_PICKLIST',
  'MULTI_ITEM',
  'MULTI_USER',
  'MULTI_CUSTOMER',
  'MULTI_TEXT',
  'MULTI_LINK',
]);

export function buildCustomFieldUpdate(
  itemId: number,
  fieldId: string,
  values: TypedFieldValue[],
  currentFieldValue?: unknown,
): VisualizationItemUpdateInput {
  const updateInput = createEmptyUpdateInput(itemId);

  for (const v of values) {
    if (MULTI_FIELD_TYPES.has(v.fieldType)) {
      const previousEntryIds = Array.isArray(currentFieldValue)
        ? (currentFieldValue as { id: number }[])
            .filter((entry) => typeof entry === 'object' && entry !== null && 'id' in entry)
            .map((entry) => entry.id)
        : [];
      updateInput.multiSelectFieldData = [
        ...(updateInput.multiSelectFieldData ?? []),
        {
          customFieldId: fieldId,
          values: v.value as (string | number | boolean)[],
          previousEntryIds,
        },
      ];
    } else {
      updateInput.customFieldData = [
        ...(updateInput.customFieldData ?? []),
        { customFieldId: fieldId, value: v.value as string | number | boolean | null },
      ];
    }
  }

  return updateInput;
}

const parseValue = (v: string): string | number => {
  const num = Number(v);
  return Number.isNaN(num) ? v : num;
};

export function applyHeaderValueToUpdate({
  updateInput,
  propertyKey,
  targetValue,
  sourceValue,
  customFields,
  noValueId,
  compositeIdSeparator,
  combineMultiSelect,
}: {
  updateInput: VisualizationItemUpdateInput;
  propertyKey: string;
  targetValue: string;
  sourceValue: string | null | undefined;
  customFields: ItemV2['customFieldValues'] | undefined;
  noValueId: string;
  compositeIdSeparator: string;
  combineMultiSelect: boolean;
}): void {
  if (propertyKey.startsWith('customFields.')) {
    const customFieldKey = propertyKey.split('.')[1] as string;
    const currentFieldValue = customFields?.[customFieldKey];
    const isMultiSelect = Array.isArray(currentFieldValue);

    if (isMultiSelect) {
      const currentPrimitives = unwrapMultiDto(currentFieldValue).filter(
        (v): v is string | number | boolean => v !== null,
      );

      if (targetValue === noValueId) {
        updateInput.multiSelectFieldData = [
          ...(updateInput.multiSelectFieldData ?? []),
          { customFieldId: customFieldKey, values: [] },
        ];
      } else if (combineMultiSelect) {
        const values = targetValue.includes(compositeIdSeparator)
          ? targetValue.split(compositeIdSeparator).map(parseValue)
          : [parseValue(targetValue)];
        updateInput.multiSelectFieldData = [
          ...(updateInput.multiSelectFieldData ?? []),
          { customFieldId: customFieldKey, values },
        ];
      } else {
        const sourceNum = Number(sourceValue);
        const targetNum = Number(targetValue);
        const newValues = sourceValue
          ? currentPrimitives.filter((v) => Number(v) !== sourceNum).concat(targetNum)
          : [...currentPrimitives, targetNum];
        updateInput.multiSelectFieldData = [
          ...(updateInput.multiSelectFieldData ?? []),
          { customFieldId: customFieldKey, values: newValues },
        ];
      }
    } else {
      const parsedCustomValue = targetValue === noValueId ? null : parseValue(targetValue);
      updateInput.customFieldData = [
        ...(updateInput.customFieldData ?? []),
        { customFieldId: customFieldKey, value: parsedCustomValue },
      ];
    }
  } else {
    const parsedValue =
      targetValue !== '' && !Number.isNaN(Number(targetValue)) ? Number(targetValue) : targetValue;
    updateInput.itemData[propertyKey] = parsedValue === noValueId ? null : parsedValue;
  }
}

export async function handleVisualizationUpdateError(error: unknown, t: TFunction): Promise<void> {
  if (error instanceof HTTPError && error.response.status === 403) {
    toast.error(
      t(($) => $.portfolio.itemPanel.errors.updateForbidden),
      {
        description: t(($) => $.portfolio.itemPanel.errors.updateForbiddenDescription),
      },
    );
    return;
  }
  throw error;
}
